"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Spaltengriff: die Breite einer Spalte ziehen
// ============================================================
// Zwischen einer Arbeitsflaeche und einer Spalte zum Nachschlagen
// daneben — auf der Projektuebersicht Spickzettel und Kommentare
// rechts neben den Aufgaben. Ansage vom 08.10.2026: „flexibler
// Handle zum verschieben der Breite“.
//
//   ZIEHEN     an der Zuglasche im Spalt, nach links breiter, nach
//              rechts schmaler — oder irgendwo sonst im Spalt, der
//              ganze Spalt ist Griff. Ein Doppelklick setzt die
//              Vorgabe zurueck.
//   TASTATUR   Fokus auf den Griff, Pfeiltasten (mit Umschalt in
//              grossen Schritten), Pos1 und Ende fuer schmal und
//              breit, Eingabetaste blendet die Spalte aus.
//
// AUSGEBLENDET wird nicht hier, sondern mit einem Schalter dort, wo
// die Ansicht gesteuert wird (Projektuebersicht: ganz rechts in der
// Zeile ueber den Aufgaben, hinter „Weiterleiten“). Ein Knopf auf dem Spalt
// sass zwischen zwei Karten und gehoerte zu keiner (Ansage: „muss ja
// nicht auf der Spalte liegen… Dann aber einen besseren Handler“).
//
// Die Lasche ist die Zuglasche des Hauses (`drag-tab.tsx`), hier
// senkrecht: dasselbe Zeichen fuer „hier wird gezogen“ wie an den
// Belegpositionen, und wie dort erst beim Hinzeigen sichtbar (Ansage:
// „erst anzeigen per hover“). Wo sie ruht, gibt der Aufrufer vor
// (`--spaltengriff-oben`); beim Rollen laeuft sie oben im Bild mit,
// damit sie bei einer langen Aufgabenliste nicht aus dem Blick geraet.
// Den Zeiger zum Ziehen zeigt der ganze Spalt auch ohne Lasche.
//
// Waehrend des Ziehens zeichnet React nichts neu: der Griff meldet
// jede Bewegung (`onZiehen`), der Aufrufer setzt damit eine
// CSS-Variable. Gemerkt wird erst beim Loslassen (`onLoslassen`).
//
// GEMERKT WIRD IM BROWSER, nicht in der Datenbank — wie das
// Zuklappen von Karten (`klappen.tsx`): wer die Spalte breiter
// zieht, raeumt SEINE Sicht auf, nicht die der Kollegen. Beim
// Serveraufbau steht die Vorgabe, gleich danach der eigene Stand.
// ============================================================

import { useRef, useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";
import { DragTab } from "./drag-tab";

const ABLAGE = "ans-breite:";
const ABLAGE_EREIGNIS = "ans-breite-stand";

/** Pfeiltaste: so viele Pixel, mit Umschalt das Vierfache. */
const SCHRITT = 16;

function standHorchen(melden: () => void) {
  window.addEventListener("storage", melden);
  window.addEventListener(ABLAGE_EREIGNIS, melden);
  return () => {
    window.removeEventListener("storage", melden);
    window.removeEventListener(ABLAGE_EREIGNIS, melden);
  };
}

/**
 * Die gemerkte Breite einer Spalte in Pixeln, und wie man sie setzt
 * (`null` nimmt sie zurueck auf die Vorgabe). Der Schluessel nennt
 * die Sache, nicht die Seite (`umfeld:projekt`) — wie bei
 * `useZugeklappt`.
 */
export function useSpaltenbreite(schluessel: string, vorgabe: number) {
  const gemerkt = useSyncExternalStore(
    standHorchen,
    () => window.localStorage.getItem(ABLAGE + schluessel),
    () => null,
  );
  const zahl = gemerkt === null ? Number.NaN : Number(gemerkt);
  const breite = Number.isFinite(zahl) && zahl > 0 ? zahl : vorgabe;

  function setzen(neu: number | null) {
    try {
      if (neu === null) window.localStorage.removeItem(ABLAGE + schluessel);
      else window.localStorage.setItem(ABLAGE + schluessel, String(Math.round(neu)));
    } catch {
      // Ohne Ablage bleibt es bei der Vorgabe — nichts geht verloren.
    }
    window.dispatchEvent(new Event(ABLAGE_EREIGNIS));
  }

  return [breite, setzen] as const;
}

export function Spaltengriff({
  name,
  breite,
  min,
  max,
  messen,
  onZiehen,
  onLoslassen,
  onZuruecksetzen,
  onAusblenden,
  className,
}: {
  /** Was in der Spalte steht — fuer die Beschriftung („Spickzettel und Kommentare“). */
  name: string;
  /** Die Breite, die gerade gilt — fuer Tastatur und Vorlesen. */
  breite: number;
  min: number;
  /** Hoechstens so breit — haengt an der Breite des Rasters, der Aufrufer misst sie. */
  max: number;
  /** Die Breite, wie sie gerade zu SEHEN ist — Startpunkt des Ziehens. */
  messen: () => number;
  /** Jede Bewegung, schon begrenzt. */
  onZiehen: (px: number) => void;
  /** Losgelassen oder per Tastatur gesetzt: das wird gemerkt. */
  onLoslassen: (px: number) => void;
  onZuruecksetzen: () => void;
  /** Eingabetaste auf dem Griff — wie der Schalter in der Ansicht. */
  onAusblenden?: () => void;
  className?: string;
}) {
  const zug = useRef<{ x: number; breite: number; zuletzt: number } | null>(null);

  const obergrenze = Math.max(min, Math.round(max));
  const begrenzt = (px: number) => Math.round(Math.min(Math.max(px, min), obergrenze));

  function aufhoeren(e: React.PointerEvent<HTMLDivElement>) {
    const lauf = zug.current;
    if (!lauf) return;
    zug.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    if (lauf.zuletzt !== lauf.breite) onLoslassen(lauf.zuletzt);
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Breite von ${name}`}
      aria-valuenow={Math.min(breite, obergrenze)}
      aria-valuemin={min}
      aria-valuemax={obergrenze}
      tabIndex={0}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const start = messen();
        zug.current = { x: e.clientX, breite: start, zuletzt: start };
        // Waehrend des Ziehens: ueberall der Zieh-Zeiger, nirgends
        // markierter Text.
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
      }}
      onPointerMove={(e) => {
        const lauf = zug.current;
        if (!lauf) return;
        // Nach links gezogen wird die Spalte rechts breiter.
        const neu = begrenzt(lauf.breite + (lauf.x - e.clientX));
        if (neu === lauf.zuletzt) return;
        lauf.zuletzt = neu;
        onZiehen(neu);
      }}
      onPointerUp={aufhoeren}
      onPointerCancel={aufhoeren}
      onDoubleClick={onZuruecksetzen}
      onKeyDown={(e) => {
        const schritt = e.shiftKey ? SCHRITT * 4 : SCHRITT;
        // Vom SICHTBAREN Stand aus: ein schmales Fenster deckelt eine
        // gemerkte grosse Breite, und jeder Druck soll etwas tun.
        const jetzt = Math.min(breite, obergrenze);
        let neu: number | null = null;
        if (e.key === "ArrowLeft") neu = jetzt + schritt;
        else if (e.key === "ArrowRight") neu = jetzt - schritt;
        else if (e.key === "Home") neu = min;
        else if (e.key === "End") neu = obergrenze;
        else if (e.key === "Enter" && onAusblenden) {
          e.preventDefault();
          onAusblenden();
          return;
        }
        if (neu === null) return;
        e.preventDefault();
        const px = begrenzt(neu);
        onZiehen(px);
        onLoslassen(px);
      }}
      className={cn(
        "group/griff relative cursor-col-resize touch-none outline-none",
        // Die Linie mittig ueber die ganze Hoehe — beim Zeigen, beim
        // Ziehen und im Fokus. Ruhend traegt nur die Lasche den Griff.
        "after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2",
        "after:duration-fast after:bg-transparent after:transition-colors",
        "hover:after:bg-border-strong focus-visible:after:bg-accent active:after:bg-accent",
        className,
      )}
    >
      {/* Wo die Lasche ruht, sagt der Aufrufer (`--spaltengriff-oben`,
          auf der Projektuebersicht die Oberkante der ersten Karte links);
          beim Rollen bleibt sie oben im Bild. Zu sehen ist sie wie jede
          Zuglasche erst beim Hinzeigen — dazu im Tastaturfokus und
          waehrend des Ziehens. */}
      <div className="sticky top-[calc(var(--ans-topbar)+16px)] z-[1] mt-[var(--spaltengriff-oben,0px)] flex justify-center">
        <DragTab
          className={cn(
            "h-10 cursor-col-resize active:cursor-col-resize",
            "group-hover/griff:opacity-100 group-focus-visible/griff:opacity-100 group-active/griff:opacity-100",
            "group-hover/griff:border-text-tertiary group-focus-visible/griff:border-accent group-active/griff:border-accent",
          )}
        />
      </div>
    </div>
  );
}
